/**
 * Cache IDB de la liste inbox, indexée par filtre actif.
 *
 * Pattern stale-while-revalidate :
 *  1. loadInbox() lit le cache instantanément → rows visibles
 *  2. fetch réseau en arrière-plan
 *  3. au retour, on update gmailState + cache
 *
 * Une entrée par filtre (= combinaison labelIds + query). Permet de
 * basculer entre "Inbox" et un split `[NaoMail]/FYI` sans perdre le
 * contenu en cache de chacun.
 *
 * Stockage : IndexedDB via idb-keyval. Quota PWA iOS ~1GB en théorie.
 * Pour 500 threads metadata : ~2.5MB. Aucun souci.
 */

import { get, set, del, keys } from "idb-keyval";
import type { InboxRow } from "./store.svelte";
import { settingsState } from "../settings.svelte";

const KEY_PREFIX = "mailfox:inbox-cache:";

interface CacheEntry {
  rows: InboxRow[];
  nextPageToken: string | null;
  /** Unix ms — sert au stale check (60s) du caller. */
  cachedAt: number;
}

/** Génère une clé déterministe à partir du filtre. */
function makeKey(labelIds: string[], query: string | null): string {
  const sortedLabels = [...labelIds].sort().join(",");
  const q = query ?? "";
  return `${KEY_PREFIX}${sortedLabels}|${q}`;
}

export async function loadCache(
  labelIds: string[],
  query: string | null,
): Promise<CacheEntry | null> {
  if (!settingsState.offlineCache) return null;
  try {
    const v = await get<CacheEntry>(makeKey(labelIds, query));
    return v ?? null;
  } catch (e) {
    console.warn("inboxCache.loadCache failed:", e);
    return null;
  }
}

export async function saveCache(
  labelIds: string[],
  query: string | null,
  rows: InboxRow[],
  nextPageToken: string | null,
): Promise<void> {
  // Réglage éteint : on n'écrit plus rien, et `clearAllCaches` a déjà purgé
  // ce qui restait au moment de la bascule.
  if (!settingsState.offlineCache) return;
  try {
    await set(makeKey(labelIds, query), {
      rows,
      nextPageToken,
      cachedAt: Date.now(),
    } satisfies CacheEntry);
  } catch (e) {
    // Quota dépassé / IDB error — non-fatal, on continue sans cache
    console.warn("inboxCache.saveCache failed:", e);
  }
}

/** Drop tous les caches inbox. Utilisé au sign-out. */
export async function clearAllCaches(): Promise<void> {
  try {
    const all = await keys();
    await Promise.all(
      all
        .filter((k) => typeof k === "string" && k.startsWith(KEY_PREFIX))
        .map((k) => del(k)),
    );
  } catch (e) {
    console.warn("inboxCache.clearAllCaches failed:", e);
  }
}
